import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import MDButton from "components/MDButton";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import orgDataService from "services/orgDataService";
import pointsService from "services/pointsService";
import geocodingService from "services/geocodingService";
import routingService, { DrivabilityResult, RouteMeasurement } from "services/routingService";
import { Client, Hub } from "common/models";
import {
  CreatePointForm,
  GeocodeCandidate,
  NearbyPoint,
  PointFieldErrors,
} from "points/models";
import {
  formatDistance,
  hasErrors,
  HUB_RADIUS_MILES,
  isNullIsland,
  NEARBY_LIMIT,
  NEARBY_RADIUS_METERS,
  parseCoordinate,
  validateCreatePoint,
} from "points/pointValidation";
import {
  buildCreatePointHubs,
  evaluateHubRadius,
  HubCandidate,
  HubRadiusVerdict,
  hubLabel,
  selectNearestHub,
  shortlistHubs,
  toHubCandidate,
} from "points/hubAssignment";
import DraggableMarkerMap, { NearbyMarker } from "common/components/maps/DraggableMarkerMap";
import NearbyPointsPanel from "./NearbyPointsPanel";

/**
 * Adds a client point.
 *
 * The screen is built around one question -- does this place already exist? -- because the
 * answer is the expensive one to get wrong. A duplicate yard is not a tidy-up job later; it
 * splits a location's trips, events and invoices across two ids that nothing reconciles.
 *
 * So the nearby check runs on the coordinate as soon as there is one, well before the form is
 * filled in, and OrderApi refuses the save with a 409 if the user never engaged with what it
 * found. Neither the client-side validation here nor the confirmation tick is a gate: the server
 * enforces the same rules and is what actually decides.
 *
 * The other thing this screen exists to stop is the coordinate itself. Around 3,376 client points
 * currently sit at latitude 0 / longitude 0 -- open water off West Africa -- because no screen
 * ever checked one. Hence a map the user must look at and a confirmation they must actively tick.
 *
 * The hub USED to be picked first, before the address, and the search was bounded to 100 miles
 * around it. That ordering existed for a good reason and the reason has not gone away: a location
 * outside every hub's radius gets no ClientPointToHub row, so it has no PointOfServiceID, and a
 * trip references PointOfServiceIDs -- it would save and then be undispatchable. But it made the
 * operator answer, before saying where the place was, a question the place itself answers.
 *
 * So the hub is now DERIVED. Picking a location defaults it to the nearest hub serving that client
 * -- by road where Mapbox can say, straight-line otherwise -- and the 100-mile rule is enforced at
 * that same moment instead of on save: a place no hub reaches is refused in the results list and
 * in the pin drag, before there is a form to fill in. See points/hubAssignment for all of that.
 */

/**
 * Clients that must not be offered.
 *
 * CSX (11) stopped being a Railcrew client in August 2026. Their points are historical: settled
 * trips and invoices still reference them, so the rows stay, but nothing new gets filed there.
 *
 * UP (2) and BNSF (46) send their own locations. geo.CreateClientPoint carries special
 * find-or-create matching for exactly these two -- UP matches on name + city + state ignoring the
 * address, BNSF on name alone once the coordinate lines up -- so a hand-made point can silently
 * ADOPT a feed-managed row instead of creating one, or become a near-duplicate the next feed run
 * fights over. Their points come from the feed.
 *
 * This list hides a screen's options. OrderApi refuses the same client ids on the way in, which
 * is what actually enforces it.
 */
const UNAVAILABLE_CLIENT_IDS = [11, 2, 46];

/** Coordinates are stored to 6dp -- about 11cm, far finer than a gate needs and the precision a
 *  Mapbox drag reports anyway. Truncating here keeps the field readable while dragging. */
const COORDINATE_PRECISION = 6;

/** How long to wait after the last coordinate change before asking what is nearby. Long enough
 *  that dragging the pin or typing a latitude does not fire a request per keystroke. */
const NEARBY_DEBOUNCE_MS = 450;

const EMPTY_FORM: CreatePointForm = {
  clientId: "",
  hubId: "",
  secondaryHubId: "",
  clientPointName: "",
  clientPointAltName1: "",
  addressLine1: "",
  city: "",
  stateAbbreviation: "",
  zipCode: "",
  latitude: "",
  longitude: "",
  isConfirmed: false,
};

/**
 * Sets the primary hub, and keeps the optional second one honest while doing it.
 *
 * The second hub is dropped when the pin has carried it out of range, or when the hub being set as
 * primary IS it. Either way the dropdown would no longer offer it, and the request would carry a
 * hub that cannot serve the location, or the same hub twice.
 */
const withPrimaryHub = (
  form: CreatePointForm,
  hubId: number,
  servableHubIds: Set<number>
): CreatePointForm => {
  const keepsSecondary =
    form.secondaryHubId !== "" &&
    Number(form.secondaryHubId) !== hubId &&
    servableHubIds.has(Number(form.secondaryHubId));

  return { ...form, hubId, secondaryHubId: keepsSecondary ? form.secondaryHubId : "" };
};

type GeocodeState = "idle" | "searching" | "done" | "error";

const AddPoint = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<CreatePointForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<PointFieldErrors>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [isHubsLoading, setIsHubsLoading] = useState(false);

  const [locateMode, setLocateMode] = useState<"address" | "coordinates">("address");
  const [addressQuery, setAddressQuery] = useState("");
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);
  const [geocodeState, setGeocodeState] = useState<GeocodeState>("idle");
  const [geocodeError, setGeocodeError] = useState<string>("");
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>("");
  // The picker is a step, not a permanent control. It collapses to the chosen match once one is
  // picked so the card reads as a decision already made, and reopens on Change.
  const [isCandidateListOpen, setIsCandidateListOpen] = useState(true);

  const [nearby, setNearby] = useState<NearbyPoint[]>([]);
  // Bumped to re-ask what is nearby without the coordinate having changed -- after activating a
  // point, so the list stops showing it as inactive.
  const [nearbyRefresh, setNearbyRefresh] = useState(0);
  const [isNearbyLoading, setIsNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState<string>("");

  // Set when OrderApi answers 409. These are the points that stopped the save; the user has to
  // acknowledge them before the form will re-submit carrying their ids.
  const [blockingNearby, setBlockingNearby] = useState<NearbyPoint[] | null>(null);
  const [isBlockingAcknowledged, setIsBlockingAcknowledged] = useState(false);

  // Whether a van can actually be driven to the coordinate. Null while unknown.
  const [drivability, setDrivability] = useState<DrivabilityResult | null>(null);

  // Road distance and drive time per hub id, for the CURRENT coordinate only. Cleared whenever the
  // pin moves, because every number in it describes where the pin used to be.
  const [hubMeasurements, setHubMeasurements] = useState<Record<number, RouteMeasurement>>({});
  const [isHubResolving, setIsHubResolving] = useState(false);

  // Whether any hub can serve this coordinate at all. Null until there is one to judge.
  const [radiusVerdict, setRadiusVerdict] = useState<HubRadiusVerdict | null>(null);

  // The second hub is a link, not a field. Most locations have exactly one, and a permanently
  // visible second dropdown reads as something that needs answering.
  const [isSecondaryHubOpen, setIsSecondaryHubOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>("");

  const latitude = parseCoordinate(form.latitude);
  const longitude = parseCoordinate(form.longitude);
  const hasCoordinate = latitude !== null && longitude !== null;
  const isPlaceholderCoordinate = hasCoordinate && isNullIsland(latitude, longitude);

  useEffect(() => {
    orgDataService
      .getAllClients()
      // Only the former-client exclusion. The /lookup/clients payload is a Lookup (Id, Name,
      // IsBargeClient) and carries no isActive flag, so filtering on one silently emptied the
      // dropdown. Every other screen in Flow takes this lookup as-is.
      .then((data) => setClients((data ?? []).filter((c) => !UNAVAILABLE_CLIENT_IDS.includes(Number(c.id)))))
      .catch(() => toast.error("Could not load the client list."));
  }, []);

  // The client decides which hubs exist to be chosen from, so it still comes first -- but it is
  // now the ONLY thing that has to be answered before searching. Changing it reloads the list,
  // and the hub is re-derived from the coordinate once the new list arrives.
  useEffect(() => {
    if (form.clientId === "") {
      setHubs([]);
      return;
    }

    // getAllHubs, not getHubsByClient: the by-client endpoint returns a Lookup -- id and name
    // only -- and this screen needs each hub's COORDINATES to anchor the search and measure the
    // 100-mile rule. /hubs returns the full Hub with latitude, longitude and its client list, so
    // the filtering happens here instead.
    setIsHubsLoading(true);
    orgDataService
      .getAllHubs()
      .then((data) =>
        setHubs(
          (data ?? []).filter(
            (h) =>
              (h.clients ?? []).some((c) => Number(c) === Number(form.clientId)) &&
              Number.isFinite(h.latitude) &&
              Number.isFinite(h.longitude)
          )
        )
      )
      .catch(() => toast.error("Could not load the hubs for that client."))
      .finally(() => setIsHubsLoading(false));
  }, [form.clientId]);

  const hubById = (hubId: number | ""): Hub | null =>
    hubId === "" ? null : hubs.find((h) => Number(h.id) === Number(hubId)) ?? null;

  const selectedHub = hubById(form.hubId);

  /**
   * A chosen hub together with what has been measured about the drive to it.
   *
   * The measurement may well be missing -- Mapbox is only asked about the shortlist, and it can
   * fail -- and that is carried through as nulls rather than filled in with the straight-line
   * figure. Road miles and crow-flies miles are not the same number and geo.ClientPointToHub
   * stores the first.
   */
  const hubCandidateFor = (hubId: number | ""): HubCandidate | null => {
    const hub = hubById(hubId);
    if (!hub || latitude === null || longitude === null) return null;
    return toHubCandidate(hub, latitude, longitude, hubMeasurements[Number(hub.id)] ?? null);
  };

  // setCoordinate is handed to the map and kept stable, so it reads the hub list through a ref
  // rather than taking it as a dependency and re-subscribing the map on every change.
  const hubsRef = useRef<Hub[]>([]);
  hubsRef.current = hubs;


  /**
   * Moving the pin invalidates the confirmation and any 409 the user was answering.
   *
   * The confirmation is a statement about one specific coordinate. Letting it survive a drag
   * would turn it into a box someone ticked once at the start, which is the passive default this
   * screen is meant to avoid. The 409 goes for the same reason -- a different coordinate is a
   * different question, and the ids OrderApi returned were about the old one.
   */
  const setCoordinate = useCallback((nextLatitude: number, nextLongitude: number) => {
    // Refused, not flagged. The pin is driven by this state, so declining to accept the drag
    // returns it to where it was -- which is the honest answer to "you cannot put it there",
    // and stops someone filling the rest of the form in against a location that can never save.
    //
    // Measured against EVERY hub serving the client rather than against one chosen up front,
    // which is what the hub-first ordering used to do. Dragging out of one hub's range and into
    // another's is a legitimate move now, and only leaving them all is refused.
    const verdict = evaluateHubRadius(hubsRef.current, nextLatitude, nextLongitude);
    if (verdict.status === "tooFar") {
      toast.warning(verdict.message);
      return;
    }

    setForm((current) => ({
      ...current,
      latitude: nextLatitude.toFixed(COORDINATE_PRECISION),
      longitude: nextLongitude.toFixed(COORDINATE_PRECISION),
      isConfirmed: false,
    }));
    setBlockingNearby(null);
    setIsBlockingAcknowledged(false);
    setSaveError("");
  }, []);

  const setField = (field: keyof CreatePointForm, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field as string]) return current;
      const next = { ...current };
      delete next[field as string];
      return next;
    });
  };

  // Typing a coordinate by hand is the same act as dragging the pin, so it clears the same state.
  const setTypedCoordinate = (field: "latitude" | "longitude", value: string) => {
    setField(field, value);
    setForm((current) => ({ ...current, isConfirmed: false }));
    setBlockingNearby(null);
    setIsBlockingAcknowledged(false);
    setSaveError("");
  };

  /**
   * Works out which hub serves this location, and whether one can.
   *
   * This is the heart of the rework. The verdict is reached SYNCHRONOUSLY, before Mapbox is asked
   * anything, because its whole value is that it lands the moment the location is chosen: an
   * operator who is going to be told "no hub reaches this" has to hear it now, not after filling
   * the form in, and not conditionally on a routing call that may never come back.
   *
   * The routing that follows only decides WHICH of the hubs in range is defaulted, and supplies
   * the distance and drive time. It is debounced, and it is allowed to fail.
   */
  const hubResolveRef = useRef(0);
  useEffect(() => {
    if (latitude === null || longitude === null || isNullIsland(latitude, longitude) || hubs.length === 0) {
      setRadiusVerdict(null);
      setHubMeasurements({});
      setDrivability(null);
      setIsHubResolving(false);
      return;
    }

    const verdict = evaluateHubRadius(hubs, latitude, longitude);
    setRadiusVerdict(verdict);

    if (verdict.status !== "ok") {
      // No hub can serve this, so there is no hub to hold. Cleared rather than left pointing at
      // one that is out of range, which would let the form look answerable when it is not.
      setForm((current) => ({ ...current, hubId: "", secondaryHubId: "" }));
      setHubMeasurements({});
      setDrivability(null);
      setIsHubResolving(false);
      return;
    }

    const shortlist = shortlistHubs(hubs, latitude, longitude);
    if (shortlist.length === 0) return;

    const servableIds = new Set(
      shortlistHubs(hubs, latitude, longitude, hubs.length).map((hub) => Number(hub.id))
    );

    // Filled in immediately from the straight-line order so the field is never blank while Mapbox
    // is being asked. The road answer usually agrees; when it does not, it replaces this below.
    setForm((current) => withPrimaryHub(current, Number(shortlist[0].id), servableIds));

    const requestId = ++hubResolveRef.current;
    setIsHubResolving(true);

    // Debounced on the same terms as the nearby lookup: a pin drag would otherwise cost a routing
    // call per hub per pixel.
    const timer = setTimeout(() => {
      Promise.all(
        shortlist.map((hub) =>
          routingService
            .describeRoute({ latitude: hub.latitude, longitude: hub.longitude }, latitude, longitude)
            .then((report) => ({ hub, report }))
        )
      ).then((reports) => {
        // A slower earlier request must not overwrite a newer answer.
        if (requestId !== hubResolveRef.current) return;

        const measurements: Record<number, RouteMeasurement> = {};
        reports.forEach(({ hub, report }) => {
          measurements[Number(hub.id)] = report.measurement;
        });
        setHubMeasurements(measurements);

        const nearest = selectNearestHub(
          reports.map(({ hub, report }) => toHubCandidate(hub, latitude, longitude, report.measurement))
        );

        if (nearest) {
          setForm((current) => withPrimaryHub(current, Number(nearest.hub.id), servableIds));
          // Can a van get there? Asked of the hub that actually won, because that is the journey
          // that has to exist. It comes off the same Directions response as the distance, so
          // reading it costs nothing extra. Applies to every client, barge included: the point is
          // where the van puts the crew down -- a landing, a dock access road, a terminal gate --
          // not the water itself.
          const winner = reports.find(({ hub }) => Number(hub.id) === Number(nearest.hub.id));
          setDrivability(winner?.report.drivability ?? null);
        }

        setIsHubResolving(false);
      });
    }, NEARBY_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [latitude, longitude, hubs]);

  /**
   * Measures a hub the operator picked by hand.
   *
   * An override, or the optional second hub, was very likely not on the shortlist, so nothing has
   * routed to it. Without this its distance and drive time would go up as nulls and the database
   * would write its 10000 placeholder for a number Mapbox could have given us.
   *
   * The result is recorded under the hub id even when the lookup failed, so a hub that cannot be
   * measured is asked about once rather than on every render.
   */
  useEffect(() => {
    if (latitude === null || longitude === null || isNullIsland(latitude, longitude)) return;

    const unmeasured = [form.hubId, form.secondaryHubId]
      .map((hubId) => hubById(hubId))
      .filter((hub): hub is Hub => hub !== null && !(Number(hub.id) in hubMeasurements));

    if (unmeasured.length === 0) return;

    let isCurrent = true;
    Promise.all(
      unmeasured.map((hub) =>
        routingService
          .describeRoute({ latitude: hub.latitude, longitude: hub.longitude }, latitude, longitude)
          .then((report) => ({ hub, report }))
      )
    ).then((reports) => {
      if (!isCurrent) return;
      setHubMeasurements((current) => {
        const next = { ...current };
        reports.forEach(({ hub, report }) => {
          next[Number(hub.id)] = report.measurement;
        });
        return next;
      });
    });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.hubId, form.secondaryHubId, hubs, hubMeasurements, latitude, longitude]);

  const runGeocode = async () => {
    const query = addressQuery.trim();
    if (!query) return;

    setGeocodeState("searching");
    setGeocodeError("");
    setCandidates([]);
    setSelectedCandidateId("");
    setIsCandidateListOpen(true);

    try {
      // The pin, when there is one, biases the POI half of the search. Brand names are the common
      // case -- there are hundreds of every truck stop and motel chain -- so without a bias the
      // answer is whichever one the index ranks first, rarely the right state.
      //
      // The hub used to provide that bias, and no longer can: it is not chosen until a location
      // has been picked. The pin is the next best anchor and covers the case that matters, which
      // is re-searching to correct a place already roughly located. A first search with no pin is
      // unbiased, so a bare brand name leans on whatever the operator typed alongside it.
      const results = await geocodingService.search(
        query,
        hasCoordinate ? { latitude: latitude as number, longitude: longitude as number } : null
      );

      // Every match is kept and shown. Results no hub can serve used to be dropped silently, which
      // left the operator retyping an address that was never going to work without being told why;
      // they are now listed and visibly refused, with the reason on them. See candidateVerdicts.
      setCandidates(results);
      setGeocodeState("done");
    } catch (e: any) {
      setGeocodeState("error");
      setGeocodeError(
        e?.response?.status
          ? `The address lookup failed (${e.response.status}).`
          : "The address lookup could not be reached."
      );
    }
  };

  const applyCandidate = (candidate: GeocodeCandidate) => {
    // The list already renders an unservable match as unselectable, so this is the belt to that
    // brace -- and the place the rule is actually enforced. Refusing here rather than accepting
    // and complaining later is the whole point of moving the check to selection time.
    const verdict = evaluateHubRadius(hubs, candidate.latitude, candidate.longitude);
    if (verdict.status === "tooFar") {
      toast.warning(verdict.message);
      return;
    }

    setSelectedCandidateId(candidate.id);
    setIsCandidateListOpen(false);
    appliedCandidateRef.current = {
      name: candidate.name,
      latitude: candidate.latitude.toFixed(COORDINATE_PRECISION),
      longitude: candidate.longitude.toFixed(COORDINATE_PRECISION),
    };
    setForm((current) => ({
      ...current,
      // Picking a different match means a different PLACE, so the fields DESCRIBING the old one
      // do not carry over: Name is repopulated from the new selection (empty when the match is an
      // address rather than a business) and the alternate name is cleared, because an alternate
      // name silently inherited from a previous pick is the one that ends up on the sign.
      //
      // Client is deliberately NOT cleared. It is an input to this search, not a description of
      // its result -- the client decides which hubs exist, and this candidate was only offered
      // because one of them can serve it. Clearing it here discarded the choice the result was
      // found under and left the form unsaveable with no visible reason.
      //
      // The hub is not cleared either, but for the opposite reason: it is about to be re-derived
      // from this coordinate, and blanking it first would flicker the field for no gain.
      clientPointName: candidate.name,
      clientPointAltName1: "",
      // A geocode fills the address fields it resolved and leaves anything it could not work out
      // to the user, rather than blanking what they have already typed.
      addressLine1: candidate.addressLine1 || current.addressLine1,
      city: candidate.city || current.city,
      stateAbbreviation: candidate.stateAbbreviation || current.stateAbbreviation,
      zipCode: candidate.zipCode || current.zipCode,
      latitude: candidate.latitude.toFixed(COORDINATE_PRECISION),
      longitude: candidate.longitude.toFixed(COORDINATE_PRECISION),
      isConfirmed: false,
    }));
    setErrors({});
    setBlockingNearby(null);
    setIsBlockingAcknowledged(false);
    setSaveError("");
  };

  // Ask what already exists as soon as there is a coordinate to ask about, debounced so a drag
  // or a typed latitude does not fire a request per change.
  //
  // Scoped to the selected client, which is also how the server-side duplicate gate searches. A
  // point is a (client x place) pair -- two railroads legitimately keep their own record of the
  // same yard under their own names and ids -- so another client's point at this coordinate is
  // not a duplicate of this one and listing it only buries the ones that are.
  /**
   * Ask what address is at the coordinate whenever it changes on its own -- a pin drag, or a
   * typed latitude and longitude.
   *
   * Without this, moving the pin leaves the address fields describing where it USED to be, and
   * entering raw coordinates leaves them empty -- and they are required and read-only, so the
   * form could not be saved at all. That was the GPS tab's dead end.
   *
   * Only fills what comes back. A coordinate in the middle of a yard often reverse-geocodes to
   * nothing, and blanking a good address because the lookup was empty would be worse than
   * leaving it slightly stale.
   */
  const reverseRequestRef = useRef(0);

  /**
   * The match last applied from the picker, and the coordinate it put the pin on.
   *
   * Used to tell "the pin moved because a match was selected" from "the user dragged it
   * somewhere else", which need opposite treatment: the first must keep the name the match
   * supplied, the second must not.
   */
  const appliedCandidateRef = useRef<{ name: string; latitude: string; longitude: string } | null>(null);
  useEffect(() => {
    if (latitude === null || longitude === null || isNullIsland(latitude, longitude)) {
      return;
    }

    // The pin is sitting exactly where a picked match put it, so the address is already known --
    // from the FORWARD lookup, which is the better answer. Reverse-geocoding the same coordinate
    // asks "what is nearest to this point", and the nearest thing is often not the thing that was
    // searched for: picking "3121 Broadway Street" came back as "110 Lone Oak Road", which is the
    // road Broadway turns into. The operator picked an address and would have been shown, and
    // saved, a different one.
    //
    // This also skips a Mapbox request whose answer we would have thrown away.
    const applied = appliedCandidateRef.current;
    if (
      applied !== null &&
      applied.latitude === latitude.toFixed(COORDINATE_PRECISION) &&
      applied.longitude === longitude.toFixed(COORDINATE_PRECISION)
    ) {
      return;
    }

    const requestId = ++reverseRequestRef.current;
    const timer = setTimeout(() => {
      // Drivability used to be asked here, against the hub chosen up front. It now comes off the
      // routing the hub-resolution effect above already does, so the two are one call rather than
      // two asking Mapbox the same question.
      geocodingService
        .reverse(latitude, longitude)
        .then((place) => {
          // A slower earlier lookup must not overwrite a newer answer.
          if (!place || requestId !== reverseRequestRef.current) return;

          setForm((current) => {
            // REPLACE, do not merge. These four fields describe ONE place, and falling back to
            // the previous value per field blends two: drag from a truck stop to a house whose
            // lookup has no postcode, and the truck stop's postcode rides along onto the house.
            // A field the lookup has nothing for is genuinely unknown here, so it reads empty.
            const next = {
              ...current,
              addressLine1: place.addressLine1,
              city: place.city,
              stateAbbreviation: place.stateAbbreviation,
              zipCode: place.zipCode,
            };

            // The name came from a business that is no longer under the pin. Cleared ONLY when it
            // is still exactly what that match supplied -- a name the operator typed or edited is
            // theirs and survives. The applied coordinate is compared so selecting a match, which
            // also moves the pin, does not immediately wipe the name it just filled in.
            const applied = appliedCandidateRef.current;
            const movedOffTheMatch =
              applied !== null &&
              (applied.latitude !== current.latitude || applied.longitude !== current.longitude);

            if (movedOffTheMatch && applied.name && current.clientPointName === applied.name) {
              next.clientPointName = "";
              appliedCandidateRef.current = null;
            }

            return next;
          });
        })
        .catch(() => {
          // Nothing to tell the user: the coordinate is still valid and still saveable.
        });
    }, NEARBY_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [latitude, longitude]);

  const nearbyRequestRef = useRef(0);
  useEffect(() => {
    if (latitude === null || longitude === null || isNullIsland(latitude, longitude)) {
      setNearby([]);
      setNearbyError("");
      setIsNearbyLoading(false);
      return;
    }

    const requestId = ++nearbyRequestRef.current;
    setIsNearbyLoading(true);
    setNearbyError("");

    const timer = setTimeout(() => {
      pointsService
        .getNearby({
          latitude,
          longitude,
          radiusMeters: NEARBY_RADIUS_METERS,
          clientId: form.clientId === "" ? undefined : Number(form.clientId),
          limit: NEARBY_LIMIT,
        })
        .then((results) => {
          // A slower earlier request must not overwrite a newer answer.
          if (requestId !== nearbyRequestRef.current) return;
          setNearby(results ?? []);
        })
        .catch(() => {
          if (requestId !== nearbyRequestRef.current) return;
          setNearby([]);
          setNearbyError("Could not check for existing locations nearby.");
        })
        .finally(() => {
          if (requestId !== nearbyRequestRef.current) return;
          setIsNearbyLoading(false);
        });
    }, NEARBY_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [latitude, longitude, form.clientId, nearbyRefresh]);

  // The 409 list takes over the panel when there is one -- those are the points the user is being
  // asked about right now.
  const shownNearby = blockingNearby ?? nearby;

  /**
   * The 100-mile rule, applied to every match in the results list.
   *
   * This is where the old hub-first search bound went. It used to drop unservable matches before
   * they were shown, which is quiet in the wrong way -- the operator saw a short list, or none at
   * all, and had nothing to tell them the address they wanted was out of range rather than
   * unknown. Now the match is listed, cannot be picked, and says why.
   */
  const candidateVerdicts = useMemo(() => {
    const verdicts: Record<string, HubRadiusVerdict> = {};
    candidates.forEach((candidate) => {
      verdicts[candidate.id] = evaluateHubRadius(hubs, candidate.latitude, candidate.longitude);
    });
    return verdicts;
  }, [candidates, hubs]);

  const servableCandidateCount = candidates.filter(
    (candidate) => candidateVerdicts[candidate.id]?.status === "ok"
  ).length;

  /**
   * The hubs that could actually serve this coordinate, nearest first.
   *
   * Both hub dropdowns offer these rather than the client's full list. A hub beyond the radius
   * gets no usable point of service, so offering one would put back the "chose it, then found out"
   * problem this rework exists to remove -- for the optional second hub just as much as the first.
   */
  const servableHubs = useMemo(
    () =>
      latitude === null || longitude === null ? [] : shortlistHubs(hubs, latitude, longitude, hubs.length),
    [hubs, latitude, longitude]
  );

  /**
   * The measured drive to a chosen hub, for the field's helper line.
   *
   * Says so plainly when there is no measurement rather than showing nothing. The operator should
   * know that what lands in geo.ClientPointToHub.Distance is the server's placeholder rather than
   * a measured figure, because that column is what mileage bills on.
   */
  const hubMeasurementLabel = (hubId: number | ""): string => {
    if (hubId === "") return " ";
    if (isHubResolving) return "Measuring the drive…";

    const candidate = hubCandidateFor(hubId);
    if (!candidate) return " ";
    if (candidate.roadMiles === null || candidate.driveTimeMinutes === null) {
      return "Drive could not be measured — the server will fill in its own distance.";
    }
    return `${candidate.roadMiles} mi · ${candidate.driveTimeMinutes} min by road`;
  };

  const nearbyMarkers: NearbyMarker[] = useMemo(
    () =>
      shownNearby.map((point) => ({
        id: point.clientPointId,
        latitude: point.latitude,
        longitude: point.longitude,
        label: point.clientPointName || `Point ${point.pointId}`,
        detail: [
          [point.addressLine1, point.city, point.stateAbbreviation].filter(Boolean).join(", "),
          formatDistance(point.distanceMeters),
          (point.hubNames ?? []).length > 0 ? point.hubNames.join(", ") : "No hubs assigned",
        ]
          .filter(Boolean)
          .join(" · "),
        isMuted: !point.isActive,
      })),
    [shownNearby]
  );

  const save = async () => {
    const validation = validateCreatePoint(form, selectedHub);
    setErrors(validation);
    setSaveError("");

    if (hasErrors(validation)) {
      toast.error("Some details still need attention.");
      return;
    }

    // A location a crew cannot be driven to is not a location. Checked here as well as shown on
    // the map card, because the map card is easy to scroll past.
    if (drivability && !drivability.isDrivable) {
      setSaveError(drivability.reason);
      toast.error("That spot cannot be driven to.");
      return;
    }

    if (blockingNearby && !isBlockingAcknowledged) {
      toast.error("Confirm the nearby locations are different before saving.");
      return;
    }

    // Refused here as well as at selection time. Nothing on the screen should be able to reach
    // this state -- the results list will not offer an out-of-range match and the pin will not
    // drag to one -- but a location that no hub can serve is exactly the row this screen exists
    // to stop being written, so it is not left to the UI alone.
    if (radiusVerdict && radiusVerdict.status === "tooFar") {
      setSaveError(radiusVerdict.message);
      toast.error("No hub can serve that location.");
      return;
    }

    setIsSaving(true);
    try {
      const outcome = await pointsService.createPoint({
        clientId: Number(form.clientId),
        clientPointName: form.clientPointName.trim(),
        clientPointAltName1: form.clientPointAltName1.trim() || undefined,
        addressLine1: form.addressLine1.trim(),
        city: form.city.trim(),
        stateAbbreviation: form.stateAbbreviation.trim().toUpperCase(),
        zipCode: form.zipCode.trim() || undefined,
        latitude: parseCoordinate(form.latitude),
        longitude: parseCoordinate(form.longitude),
        // The hub, and its measured road distance and drive time. Sent because the database
        // otherwise writes a 10000 placeholder into geo.ClientPointToHub.Distance -- the column
        // mileage bills on. A hub Mapbox could not measure goes up with nulls and the server
        // falls back to that placeholder, which is the right answer to "we do not know" and not
        // a reason to refuse the save.
        hubs: buildCreatePointHubs(hubCandidateFor(form.hubId), hubCandidateFor(form.secondaryHubId)),
        // Only the ids the user has actually been shown and dismissed. Empty on a first attempt,
        // which is what makes the server's 409 possible in the first place.
        acknowledgedNearbyClientPointIds:
          blockingNearby && isBlockingAcknowledged
            ? blockingNearby.map((point) => point.clientPointId)
            : [],
      });

      switch (outcome.status) {
        case "created":
          // A point with no hub saves, but cannot be dispatched to until one is added -- the
          // point's own screen is where that happens, which is where this navigates. Shown as a
          // warning that persists rather than a success that disappears: the operator has one
          // more thing to do and needs to still see that after the page changes.
          if (outcome.point.pointOfServiceIds?.length === 0) {
            toast.warning(
              outcome.point.message ||
                `${outcome.point.clientPointName || "Location"} added, but no hub serves it yet.`,
              { autoClose: false }
            );
          } else {
            toast.success(`${outcome.point.clientPointName || "Location"} added.`);
          }
          // ClientPointID, NOT PointID. The points screens address a point by its ClientPointID
          // throughout -- the search projects `PointId = clientPoint.Id` and the detail endpoint
          // looks up ClientPoints by that same id -- so the field named "pointId" in a search
          // result is a ClientPointID wearing the wrong name. The create response is the one
          // place both ids are present and distinct, which is why sending the real geo.Point id
          // here loaded the screen and found nothing.
          navigate(`/points/${outcome.point.clientPointId}`);
          return;

        case "conflict":
          // Not an error. The server is asking the user to look at something.
          setBlockingNearby(outcome.nearby ?? []);
          setIsBlockingAcknowledged(false);
          toast.warning("Existing locations were found nearby. Review them before saving.");
          return;

        case "invalid":
          setErrors((current) => ({ ...current, ...outcome.fieldErrors }));
          setSaveError(outcome.message || "The server rejected some of these details.");
          return;

        default:
          setSaveError(outcome.message);
          toast.error(outcome.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Field errors the form has no input for. A validation message with nowhere to land still has
  // to be readable, so it is shown rather than dropped.
  const unmappedErrors = Object.keys(errors).filter((key) => !(key in EMPTY_FORM));

  const selectedCandidate = candidates.find((candidate) => candidate.id === selectedCandidateId);

  const fieldProps = (field: keyof CreatePointForm) => ({
    error: Boolean(errors[field as string]),
    helperText: errors[field as string] ?? " ",
  });

  /**
   * The address and the coordinate are OWNED by step 1 -- they arrive from a geocode match or
   * from the pin, and are shown here as what is about to be saved rather than as inputs.
   *
   * Read-only rather than disabled on purpose. A disabled field greys its value out and drops it
   * from the tab order, which reads as "this is not part of the record"; read-only keeps the
   * value legible, selectable and copyable while refusing edits. The user changes any of it by
   * picking a different match or dragging the pin, which is the only path that keeps the address
   * and the coordinate describing the same place.
   */
  const searchOwnedProps = {
    InputProps: { readOnly: true },
    sx: { "& .MuiInputBase-root": { backgroundColor: "action.hover" } },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4">Add a Location</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Place the pin on the map and check what already exists there before saving.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <MDButton component={Link} to="/points" variant="outlined" color="secondary">
          Cancel
        </MDButton>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1 · Find the location
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                Fields marked * are required.
              </Typography>

              {/* The client comes first and gates everything after it, because it decides which
                  hubs exist. The HUB no longer does: it is derived from the location further down,
                  which is the one thing this screen changed. What the hub-first ordering was
                  protecting -- geo.CreateClientPoint attaches a point to the nearest hub serving
                  the client within 100 miles, and a point with no hub has no PointOfServiceID, so
                  no trip can reference it -- is now enforced on each match instead. */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControl required fullWidth size="small" error={Boolean(errors.clientId)}>
                    <InputLabel id="add-point-client">Client</InputLabel>
                    <Select
                      labelId="add-point-client"
                      label="Client"
                      value={form.clientId}
                      onChange={(e) => {
                        // A different client means a different hub list, so both hubs are cleared
                        // -- they belong to the old client and the new list will re-derive them.
                        // The RECORD is left alone: the address and coordinate already found are
                        // still a real place, and step 1 is a search tool, not the record. Wiping
                        // the details here made re-picking a client feel like the form had thrown
                        // the work away.
                        //
                        // The coordinate is re-judged against the new client's hubs as soon as
                        // they load, and save validates the record, so nothing unsafe survives.
                        setForm((current) => ({
                          ...current,
                          clientId: e.target.value as any,
                          hubId: "",
                          secondaryHubId: "",
                        }));
                        setIsSecondaryHubOpen(false);
                        setHubMeasurements({});
                        setErrors({});
                        setSaveError("");
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={Number(client.id)}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.clientId ?? " "}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              {form.clientId === "" ? (
                <Alert severity="info">
                  Choose a client, then search for the address. The hub that serves this location is
                  worked out from where it is — locations still have to be within {HUB_RADIUS_MILES}{" "}
                  miles of a hub, but you do not have to pick one first.
                </Alert>
              ) : (
              <>
              {!isHubsLoading && hubs.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This client has no hubs with coordinates on record, so nothing can be set up to
                  serve a location. A hub has to be added before locations can be filed here.
                </Alert>
              )}

              {/* The pin itself is out of range. The results list refuses an unservable match and
                  the map refuses a drag, so the way into this state is typing coordinates on the
                  GPS tab -- the one path neither of those can police. */}
              {radiusVerdict?.status === "tooFar" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <AlertTitle>No hub can serve this location</AlertTitle>
                  {radiusVerdict.message}
                </Alert>
              )}

              <Tabs
                value={locateMode}
                onChange={(_e, value) => setLocateMode(value)}
                sx={{ mb: 2 }}
              >
                <Tab value="address" label="Address" />
                <Tab value="coordinates" label="GPS coordinates" />
              </Tabs>

              {locateMode === "address" ? (
                <>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address or place"
                      placeholder="e.g. 2500 E Front St, North Platte, NE"
                      value={addressQuery}
                      onChange={(e) => setAddressQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          runGeocode();
                        }
                      }}
                    />
                    <MDButton
                      variant="contained"
                      color="info"
                      onClick={runGeocode}
                      disabled={geocodeState === "searching" || !addressQuery.trim()}
                    >
                      {geocodeState === "searching" ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )}
                    </MDButton>
                  </Stack>

                  {geocodeState === "error" && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {geocodeError} You can still enter the coordinates directly on the GPS tab.
                    </Alert>
                  )}

                  {geocodeState === "done" && candidates.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <AlertTitle>No match</AlertTitle>
                      Nothing was found for that address. Yard and gate names are often not in the
                      address database — try a nearby street address, or switch to the GPS tab and
                      enter the coordinates directly.
                    </Alert>
                  )}

                  {/* The whole of the 100-mile guard, at the moment of choosing. A match no hub
                      can reach is listed but not selectable, and carries the reason -- so the
                      operator learns it here rather than after filling the form in. */}
                  {candidates.length > 0 && servableCandidateCount === 0 && isCandidateListOpen && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <AlertTitle>Out of range</AlertTitle>
                      No hub serving this client is within {HUB_RADIUS_MILES} miles of any of these
                      matches, so none of them can be added. Search closer to a hub, or get a hub set
                      up for this area first.
                    </Alert>
                  )}

                  {candidates.length > 0 && isCandidateListOpen && (
                    <>
                      <Typography variant="caption" sx={{ color: "text.secondary", mt: 2, display: "block" }}>
                        Pick the closest match, then drag the pin to the exact spot.
                      </Typography>
                      <List dense sx={{ maxHeight: 220, overflowY: "auto" }}>
                        {candidates.map((candidate) => {
                          const verdict = candidateVerdicts[candidate.id];
                          const isServable = verdict?.status === "ok";

                          return (
                            <ListItemButton
                              key={candidate.id}
                              selected={candidate.id === selectedCandidateId}
                              disabled={!isServable}
                              onClick={() => applyCandidate(candidate)}
                            >
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={candidate.kind === "poi" ? "info" : "default"}
                                      label={candidate.typeLabel}
                                    />
                                    <Typography variant="body2">{candidate.label}</Typography>
                                  </Stack>
                                }
                                secondary={
                                  verdict?.status === "tooFar"
                                    ? `No hub within ${HUB_RADIUS_MILES} miles — the nearest, ${hubLabel(
                                        verdict.hub
                                      )}, is about ${Math.round(verdict.miles)} miles away`
                                    : verdict?.status === "noHubs"
                                    ? "This client has no hubs with coordinates"
                                    : `${candidate.latitude.toFixed(5)}, ${candidate.longitude.toFixed(5)}`
                                }
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </>
                  )}

                  {/* Once a match is applied the list has done its job, so it collapses to the
                      choice itself. The other matches are kept in state rather than refetched --
                      Change puts the same list back, still showing which one is applied. */}
                  {selectedCandidate && !isCandidateListOpen && (
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={1}
                      sx={{
                        mt: 2,
                        p: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                          Address used
                        </Typography>
                        <Typography variant="body2">{selectedCandidate.label}</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {selectedCandidate.latitude.toFixed(5)}, {selectedCandidate.longitude.toFixed(5)}
                        </Typography>
                      </Box>
                      {candidates.length > 1 && (
                        <MDButton
                          size="small"
                          variant="text"
                          color="info"
                          onClick={() => setIsCandidateListOpen(true)}
                        >
                          Change
                        </MDButton>
                      )}
                    </Stack>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Enter the coordinates in the Latitude and Longitude fields below. The pin and the
                  nearby check follow whatever you type.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Latitude"
                    value={form.latitude}
                    onChange={(e) => setTypedCoordinate("latitude", e.target.value)}
                    {...fieldProps("latitude")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Longitude"
                    value={form.longitude}
                    onChange={(e) => setTypedCoordinate("longitude", e.target.value)}
                    {...fieldProps("longitude")}
                  />
                </Grid>
              </Grid>
              </>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                3 · Location details
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                Fields marked * are required.
              </Typography>

              {/* Nothing here until a place has been chosen. These fields describe ONE location,
                  and showing them half-filled from a client and a hub -- before anything has been
                  selected -- reads as a form waiting to be typed into, which is exactly what it
                  is not. Everything except the name comes from the selection. */}
              {!hasCoordinate || isPlaceholderCoordinate ? (
                <Alert severity="info">
                  Select an address or place in step 1 and its details will appear here.
                </Alert>
              ) : radiusVerdict?.status === "tooFar" ? (
                /* Refused before the form appears rather than on save. Typing a name and an
                   alternate name for a location that can never be served is precisely the wasted
                   work the hub-first ordering used to prevent, and this is where that protection
                   is repaid. */
                <Alert severity="error">
                  <AlertTitle>No hub can serve this location</AlertTitle>
                  {radiusVerdict.message}
                </Alert>
              ) : (
              <>
              <Grid container spacing={1}>
                {/* The client is chosen in step 1 and mirrored here read-only, because it is part
                    of the ROW -- ClientID goes on geo.ClientPoint -- and this card shows everything
                    that gets written. The HUB is different: it lives here, and it is a real control
                    rather than a mirror. It is what geo.ClientPointToHub is created against, which
                    is the PointOfServiceID a trip references. */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Client"
                    value={clients.find((c) => Number(c.id) === Number(form.clientId))?.name ?? ""}
                    {...searchOwnedProps}
                    {...fieldProps("clientId")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    required
                    fullWidth
                    size="small"
                    error={Boolean(errors.hubId)}
                    disabled={isHubsLoading || servableHubs.length === 0}
                  >
                    <InputLabel id="add-point-hub">Hub</InputLabel>
                    <Select
                      labelId="add-point-hub"
                      label="Hub"
                      value={form.hubId}
                      onChange={(e) => {
                        setField("hubId", e.target.value as any);
                        // Promoting the second hub to primary must not leave it in both fields.
                        if (Number(e.target.value) === Number(form.secondaryHubId)) {
                          setField("secondaryHubId", "");
                        }
                      }}
                    >
                      {servableHubs.map((hub) => (
                        <MenuItem key={hub.id} value={Number(hub.id)}>
                          {hubLabel(hub)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.hubId ?? hubMeasurementLabel(form.hubId)}</FormHelperText>
                  </FormControl>

                  {/* A link, not a second dropdown. Nearly every location is served from one hub,
                      and a permanently visible second field reads as something that has to be
                      answered -- which is how empty-but-required-looking fields get filled in with
                      whatever is at the top of the list. */}
                  {!isSecondaryHubOpen && (
                    <MDButton
                      size="small"
                      variant="text"
                      color="info"
                      disabled={servableHubs.length < 2}
                      onClick={() => setIsSecondaryHubOpen(true)}
                    >
                      Associate another hub
                    </MDButton>
                  )}
                </Grid>

                {isSecondaryHubOpen && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={servableHubs.length < 2}>
                      <InputLabel id="add-point-second-hub">Second hub</InputLabel>
                      <Select
                        labelId="add-point-second-hub"
                        label="Second hub"
                        value={form.secondaryHubId}
                        onChange={(e) => setField("secondaryHubId", e.target.value as any)}
                      >
                        {servableHubs
                          .filter((hub) => Number(hub.id) !== Number(form.hubId))
                          .map((hub) => (
                            <MenuItem key={hub.id} value={Number(hub.id)}>
                              {hubLabel(hub)}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{hubMeasurementLabel(form.secondaryHubId)}</FormHelperText>
                    </FormControl>

                    <MDButton
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        setIsSecondaryHubOpen(false);
                        setField("secondaryHubId", "");
                      }}
                    >
                      Remove
                    </MDButton>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Name"
                    value={form.clientPointName}
                    onChange={(e) => setField("clientPointName", e.target.value)}
                    {...fieldProps("clientPointName")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Alternate name (optional)"
                    value={form.clientPointAltName1}
                    onChange={(e) => setField("clientPointAltName1", e.target.value)}
                    {...fieldProps("clientPointAltName1")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Address"
                    value={form.addressLine1}
                    {...searchOwnedProps}
                    {...fieldProps("addressLine1")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="City"
                    value={form.city}
                    {...searchOwnedProps}
                    {...fieldProps("city")}
                  />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="State"
                    value={form.stateAbbreviation}
                    {...searchOwnedProps}
                    {...fieldProps("stateAbbreviation")}
                  />
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Zip (optional)"
                    value={form.zipCode}
                    {...searchOwnedProps}
                    {...fieldProps("zipCode")}
                  />
                </Grid>

                {/* The coordinate that will actually be written, mirrored down here with the rest
                    of the record. Editing still happens in step 1 -- the GPS tab or the pin. */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Latitude"
                    value={form.latitude}
                    {...searchOwnedProps}
                    {...fieldProps("latitude")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Longitude"
                    value={form.longitude}
                    {...searchOwnedProps}
                    {...fieldProps("longitude")}
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Client, address and coordinates come from step 1 — change them there. The hub is the
                nearest one to this location by road; change it here if a different one serves it.
              </Typography>
              </>
              )}

              {unmappedErrors.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {unmappedErrors.map((key) => (
                    <div key={key}>{errors[key]}</div>
                  ))}
                </Alert>
              )}

              {saveError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {saveError}
                </Alert>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Box sx={{ flex: 1 }} />
                <MDButton component={Link} to="/points" variant="outlined" color="secondary">
                  Cancel
                </MDButton>
                <MDButton variant="contained" color="primary" onClick={save} disabled={isSaving}>
                  {isSaving ? "Saving…" : "Save Location"}
                </MDButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                2 · Confirm the pin
              </Typography>

              {hasCoordinate && !isPlaceholderCoordinate ? (
                <>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    Drag the blue pin to the exact gate or entrance. Red pins are locations that
                    already exist. Switch to satellite when the street map is not enough to tell.
                  </Typography>

                  <DraggableMarkerMap
                    latitude={latitude}
                    longitude={longitude}
                    onDragEnd={setCoordinate}
                    nearby={nearbyMarkers}
                    height="340px"
                  />

                  {drivability && !drivability.isDrivable && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {drivability.reason}
                    </Alert>
                  )}

                  {/* Active, not passive. This resets every time the pin moves, so it is always a
                      statement about the coordinate actually being saved. */}
                  <FormControlLabel
                    // The app theme overrides MuiFormControlLabel root to display:block, which
                    // drops the flex row that normally sits the control beside its label -- so
                    // every checkbox stacks unless the row is put back per instance. Overridden
                    // here rather than in the theme: that default is load-bearing for the stacked
                    // checkbox lists elsewhere in Flow.
                    sx={{ mt: 1, display: "flex", alignItems: "center" }}
                    control={
                      <Checkbox
                        checked={form.isConfirmed}
                        onChange={(e) => setField("isConfirmed", e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I have looked at the map and the pin is in the right place.
                      </Typography>
                    }
                  />
                  {errors.isConfirmed && (
                    <FormHelperText error sx={{ ml: 4 }}>
                      {errors.isConfirmed}
                    </FormHelperText>
                  )}
                </>
              ) : (
                <Alert severity={isPlaceholderCoordinate ? "error" : "info"} sx={{ my: 2 }}>
                  {isPlaceholderCoordinate
                    ? "Latitude 0 / longitude 0 is open water in the Gulf of Guinea, not a location. Search an address or enter the real coordinates."
                    : "No coordinate yet. Search an address or enter a latitude and longitude to place the pin."}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Already recorded near here
              </Typography>

              <NearbyPointsPanel
                nearby={shownNearby}
                isLoading={isNearbyLoading && !blockingNearby}
                error={nearbyError}
                hasCoordinate={hasCoordinate && !isPlaceholderCoordinate}
                isBlocking={Boolean(blockingNearby)}
                isAcknowledged={isBlockingAcknowledged}
                onAcknowledgedChange={setIsBlockingAcknowledged}
                onActivated={() => {
                  // The point the save was refused over now exists and is active, so the refusal
                  // no longer stands: drop the 409 state and re-ask what is nearby rather than
                  // leaving the operator looking at a stale "this blocked your save" list.
                  setBlockingNearby(null);
                  setIsBlockingAcknowledged(false);
                  setSaveError("");
                  setNearbyRefresh((n) => n + 1);
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddPoint;
